import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Autosuggest from 'react-autosuggest';
import './BreedLitter.css';

const LitterForm = ({
  playerId, dogs, kennels, otherKennels, dogLookup, sireId, damId, puppyCount, optOutHealthTests,
  designerUsername, bredBy, coBreederKennelId, designerSuggestions, setSireId, setDamId,
  setPuppyCount, setOptOutHealthTests, setDesignerUsername, setBredBy, setCoBreederKennelId,
  setDesignerId, setDesignerSuggestions, setMessage, setPreviewLitter, submitLitter, previewBreeding,
  renderHealthTestsTable, fetchDesignerSuggestions, previewLitter, isInbred, commonAncestors
}) => (
  <div className="submit-litter">
    <h2>Submit New Litter</h2>
    {isInbred && (
      <p className="error-message">
        Warning: This pairing is inbred. Common ancestors: {commonAncestors.join(', ')}.
      </p>
    )}
    <div className="breeding-preview-container">
      <div className="parent-preview">
        {sireId && dogLookup[sireId] ? (
          <div className="parent-image">
            <Link to={`/dog/${sireId}`}>
              <img
                src={dogLookup[sireId]?.artUrl || 'https://via.placeholder.com/100?text=Sire'}
                alt="Sire"
                onError={e => (e.target.src = 'https://via.placeholder.com/100?text=Sire')}
              />
            </Link>
          </div>
        ) : (
          <p>Select a Sire</p>
        )}
        <span className="parent-divider">X</span>
        {damId && dogLookup[damId] ? (
          <div className="parent-image">
            <Link to={`/dog/${damId}`}>
              <img
                src={dogLookup[damId]?.artUrl || 'https://via.placeholder.com/100?text=Dam'}
                alt="Dam"
                onError={e => (e.target.src = 'https://via.placeholder.com/100?text=Dam')}
              />
            </Link>
          </div>
        ) : (
          <p>Select a Dam</p>
        )}
      </div>
      {(sireId && dogLookup[sireId]) || (damId && dogLookup[damId]) ? (
        <div className="parent-health-tests">
          <div className="parent-health-tests-sire">
            {sireId && dogLookup[sireId] && (
              <>
                <h3>Sire Health Tests</h3>
                {renderHealthTestsTable(
                  (dogLookup[sireId]?.healthTestRecords || []).filter(test => test && !test.testName.toLowerCase().startsWith('body scores')),
                  'BBP Tests',
                  'Sire',
                  dogLookup[sireId]
                )}
                {renderHealthTestsTable(
                  (dogLookup[sireId]?.healthTestRecords || []).filter(test => test && test.testName.toLowerCase().startsWith('body scores')),
                  'Body Scores',
                  'Sire',
                  dogLookup[sireId]
                )}
              </>
            )}
          </div>
          <div className="parent-health-tests-dam">
            {damId && dogLookup[damId] && (
              <>
                <h3>Dam Health Tests</h3>
                {renderHealthTestsTable(
                  (dogLookup[damId]?.healthTestRecords || []).filter(test => test && !test.testName.toLowerCase().startsWith('body scores')),
                  'BBP Tests',
                  'Dam',
                  dogLookup[damId]
                )}
                {renderHealthTestsTable(
                  (dogLookup[damId]?.healthTestRecords || []).filter(test => test && test.testName.toLowerCase().startsWith('body scores')),
                  'Body Scores',
                  'Dam',
                  dogLookup[damId]
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
      {previewLitter && (
        <div className="preview-litter">
          <h3>Preview Litter</h3>
          {previewLitter.optOutHealthTests && (
            <p className="text-yellow-600 text-sm mb-2">
              This litter has opted out of DFG inherited disease testing.
            </p>
          )}
          <div className="puppies-container">
            {previewLitter.puppies.map((puppy, idx) => (
              <div key={idx} className={`puppy-container ${puppy.gender === 'Female' ? 'female' : 'male'}`}>
                <div className="puppy-name">
                  {puppy.name} ({puppy.breed}, {puppy.color ? `${puppy.color} ${puppy.gender}` : puppy.gender})
                </div>
                <div className="puppy-details-readonly">
                  <div className="detail-row">
                    <span className="detail-label">Registered Name:</span>
                    <span className="detail-value">{puppy.registeredName || 'Not set'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Personality:</span>
                    <span className="detail-value">{puppy.personality || 'Not set'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phenotype:</span>
                    <span className="detail-value">{puppy.phenotype || 'Not set'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Genotype:</span>
                    <span className="detail-value">{puppy.genotype || 'Not set'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Color:</span>
                    <span className="detail-value">{puppy.color || 'Not set'}</span>
                  </div>
                </div>
                {!previewLitter.optOutHealthTests && puppy.healthTestRecords?.length > 0 && (
                  renderHealthTestsTable(
                    puppy.healthTestRecords.filter(test => test && !test.testName.toLowerCase().startsWith('body scores')),
                    'BBP Tests',
                    `Puppy ${idx + 1}`,
                    puppy
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    <div className="form-group">
      <label>Sire</label>
      <select value={sireId} onChange={e => setSireId(e.target.value)} className="select-input">
        <option value="">Select a Sire</option>
        {dogs.filter(d => d.gender === 'Male' && ['Approved', 'Retired'].includes(d.status)).map(dog => (
          <option key={dog.dogId} value={dog.dogId}>
            {dog.registeredName} ({dog.dogId}) {dog.isPermission ? '(Permission)' : ''}
          </option>
        ))}
      </select>
    </div>
    <div className="form-group">
      <label>Dam</label>
      <select value={damId} onChange={e => setDamId(e.target.value)} className="select-input">
        <option value="">Select a Dam</option>
        {dogs.filter(d => d.gender === 'Female' && ['Approved', 'Retired'].includes(d.status)).map(dog => (
          <option key={dog.dogId} value={dog.dogId}>
            {dog.registeredName} ({dog.dogId}) {dog.isPermission ? '(Permission)' : ''}
          </option>
        ))}
      </select>
    </div>
    <div className="form-group">
      <label>Puppy Count</label>
      <select
        className="select-input"
        value={puppyCount}
        onChange={e => setPuppyCount(parseInt(e.target.value))}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
          <option key={num} value={num}>{num} Puppies</option>
        ))}
      </select>
    </div>
    <div className="form-group">
      <label>
        <input
          type="checkbox"
          checked={optOutHealthTests}
          onChange={e => setOptOutHealthTests(e.target.checked)}
          className="mr-2"
        />
        Opt out of inherited disease testing through DFG
      </label>
      {optOutHealthTests && (
        <p className="text-yellow-600 text-sm mt-1">
          Warning: Opting out of DFG inherited disease testing may cause erroneous BBP health test inheritance in future generations. Use this only if you do not plan to test dogs using DFG health testing.
        </p>
      )}
    </div>
    <div className="form-group">
      <label>Designer Username (optional)</label>
      <Autosuggest
        suggestions={designerSuggestions}
        onSuggestionsFetchRequested={({ value }) => fetchDesignerSuggestions(value)}
        onSuggestionsClearRequested={() => setDesignerSuggestions([])}
        getSuggestionValue={suggestion => suggestion}
        renderSuggestion={suggestion => <div>{suggestion}</div>}
        inputProps={{
          value: designerUsername,
          onChange: (event, { newValue }) => {
            setDesignerUsername(newValue);
            const selected = designerSuggestions.find(s => s === newValue);
            setDesignerId(selected ? newValue : '');
          },
          className: "text-input w-full",
          placeholder: "Enter designer username"
        }}
      />
    </div>
    <div className="form-group">
      <label>Bred By Kennel</label>
      <select value={bredBy} onChange={e => setBredBy(e.target.value)} className="select-input">
        <option value="">Select Kennel</option> 
        {kennels.map(kennel => (
          <option key={kennel._id} value={kennel._id}>{kennel.kennelName}</option>
        ))}
      </select>
    </div>
    <div className="form-group">
      <label>Co-Breeder Kennel (optional)</label>
      <select
        value={coBreederKennelId}
        onChange={e => setCoBreederKennelId(e.target.value)}
        className="select-input"
      >
        <option value="">None</option>
        {otherKennels.map(kennel => (
          <option key={kennel._id} value={kennel._id}>
            {kennel.kennelName} (Owner: {kennel.playerId?.username || 'Unknown'})
          </option>
        ))}
      </select>
    </div>
    <div className="submit-actions">
      <button className="action-button" onClick={submitLitter}>Submit Litter</button>
      <button
        className="action-button preview-button"
        onClick={previewBreeding}
        disabled={!sireId || !damId}
      >
        Preview Breeding
      </button>
    </div>
  </div>
);

function BreedLitter({ playerId }) {
  const [litters, setLitters] = useState([]);
  const [message, setMessage] = useState('');
  const [sireId, setSireId] = useState('');
  const [damId, setDamId] = useState('');
  const [puppyCount, setPuppyCount] = useState(1);
  const [optOutHealthTests, setOptOutHealthTests] = useState(false);
  const [designerUsername, setDesignerUsername] = useState('');
  const [dogs, setDogs] = useState([]);
  const [previewLitter, setPreviewLitter] = useState(null);
  const [kennels, setKennels] = useState([]);
  const [bredBy, setBredBy] = useState('');
  const [coBreederKennelId, setCoBreederKennelId] = useState('');
  const [designerId, setDesignerId] = useState('');
  const [designerSuggestions, setDesignerSuggestions] = useState([]);
  const [dogLookup, setDogLookup] = useState({});
  const [kennelLookup, setKennelLookup] = useState({});
  const [otherKennels, setOtherKennels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInbred, setIsInbred] = useState(false);
  const [commonAncestors, setCommonAncestors] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!playerId) return;

    const fetchLitters = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/my-litters/${playerId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        setMessage(`Error fetching litters: ${error.message}`);
        return [];
      }
    };

    const fetchDogs = async () => {
      try {
        // Fetch owned dogs
        const ownedRes = await fetch(`${API_BASE_URL}/my-dogs-for-litters/${playerId}`);
        if (!ownedRes.ok) throw new Error(`Fetch failed: ${await ownedRes.text()}`);
        const ownedDogs = await ownedRes.json();
        console.log('Owned Dogs:', ownedDogs);

        // Fetch permitted dogs
        const permittedRes = await fetch(`${API_BASE_URL}/my-received-permissions/${playerId}`);
        if (!permittedRes.ok) throw new Error(`Fetch failed: ${await permittedRes.text()}`);
        const permissions = await permittedRes.json();
        console.log('Permissions:', permissions);

        const permittedDogIds = permissions.filter(p => !p.used).map(p => p.dogId);
        const permittedDogs = await Promise.all(
          permittedDogIds.map(async dogId => {
            try {
              const res = await fetch(`${API_BASE_URL}/public-dog/${dogId}`);
              if (!res.ok) {
                console.error(`Failed to fetch dog ${dogId}: ${await res.text()}`);
                return null;
              }
              const dog = await res.json();
              return { ...dog, isPermission: true };
            } catch (error) {
              console.error(`Error fetching dog ${dogId}:`, error.message);
              return null;
            }
          })
        );

        const allDogs = [
          ...ownedDogs.filter(d => ['Approved', 'Retired'].includes(d.status)),
          ...permittedDogs.filter(d => d && ['Approved', 'Retired'].includes(d.status))
        ];
        console.log('All Dogs:', allDogs);
        return allDogs;
      } catch (error) {
        console.error('Error fetching dogs:', error.message);
        setMessage('Error fetching dogs: ' + error.message);
        return [];
      }
    };

    const fetchKennels = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/my-approved-kennels/${playerId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        setMessage('Error fetching kennels: ' + error.message);
        return [];
      }
    };

    const fetchOtherKennels = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/other-kennels/${playerId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        setMessage('Error fetching other kennels: ' + error.message);
        return [];
      }
    };

    const fetchDogDetails = async (dogId) => {
      try {
        const res = await fetch(`${API_BASE_URL}/public-dog/${dogId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        console.error(`Error fetching dog ${dogId}:`, error.message);
        return null;
      }
    };

    const fetchKennelDetails = async (kennelId) => {
      try {
        const res = await fetch(`${API_BASE_URL}/public-kennel/${kennelId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        return await res.json();
      } catch (error) {
        console.error(`Error fetching kennel ${kennelId}:`, error.message);
        return null;
      }
    };

    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const [littersData, dogsData, kennelsData, otherKennelsData] = await Promise.all([
          fetchLitters(),
          fetchDogs(),
          fetchKennels(),
          fetchOtherKennels(),
        ]);
        setLitters(littersData);
        setDogs(dogsData);
        setKennels(kennelsData);
        setOtherKennels(otherKennelsData);

        const dogIds = new Set(dogsData.map(d => d.dogId));
        littersData.forEach(litter => {
          if (litter.sireId) dogIds.add(litter.sireId);
          if (litter.damId) dogIds.add(litter.damId);
        });

        const dogData = await Promise.all(
          [...dogIds].map(dogId => fetchDogDetails(dogId).then(data => [dogId, data]))
        );
        const kennelData = await Promise.all(
          [...kennelsData, ...otherKennelsData].map(kennel => [kennel._id, kennel])
        );

        setDogLookup(Object.fromEntries(dogData.filter(([_, data]) => data)));
        setKennelLookup(Object.fromEntries(
          kennelData.map(([id, data]) => [id, data.kennelName])
        ));
      } catch (error) {
        setMessage('Error fetching data: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [playerId]);

  useEffect(() => {
    setPreviewLitter(null);
  }, [sireId, damId, puppyCount, optOutHealthTests]);

  useEffect(() => {
    if (!sireId || !damId) {
      setIsInbred(false);
      setCommonAncestors([]);
      return;
    }

    const checkInbreeding = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/check-inbreeding/${sireId}/${damId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        const { isInbred, commonAncestors } = await res.json();
        setIsInbred(isInbred);
        setCommonAncestors(commonAncestors);
      } catch (error) {
        setMessage('Error checking inbreeding: ' + error.message);
      }
    };

    checkInbreeding();
  }, [sireId, damId]);

const submitLitter = async () => {
  if (!sireId || !damId || !bredBy) {
    setMessage('Please select a sire, dam, and bred-by kennel.');
    return;
  }

  try {
    // Build payload cleanly – omit coBreederKennelId completely when "None"
    const payload = {
      playerId,
      sireId,
      damId,
      puppyCount,
      optOutHealthTests,
      designerId,
      bredBy,
    };

    // ONLY add coBreederKennelId if the user actually picked one
    if (coBreederKennelId && coBreederKennelId.trim() !== '') {
      payload.coBreederKennelId = coBreederKennelId;
    }
    // ← If "None" was chosen, we do NOT include the key at all → schema default = 'Approved'

    const res = await fetch(`${API_BASE_URL}/litter-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!res.ok) throw new Error(await res.text());

    const data = await res.json();
    let litterId = data.litterId;
    if (litterId.startsWith('litter')) {
      litterId = litterId.replace(/^litter/, '');
    }
    if (!litterId) throw new Error('Litter ID not found in response');

    setMessage(data.message || 'Litter submitted successfully!');

    // Update local state using the REAL value from the server (best practice)
    setLitters([...litters, {
      playerId,
      sireId,
      damId,
      puppies: Array(puppyCount).fill({ owner: playerId }),
      _id: litterId,
      status: 'Pending',
      optOutHealthTests,
      designerId: data.litter?.designerId || data.designerId || playerId,
      bredBy,
      coBreederKennelId: data.litter?.coBreederKennelId || null,
      coBreederApprovalStatus: data.litter?.coBreederApprovalStatus || 'Approved'
    }]);

    // Reset form
    setSireId('');
    setDamId('');
    setPuppyCount(1);
    setOptOutHealthTests(false);
    setDesignerUsername('');
    setBredBy('');
    setCoBreederKennelId('');
    setPreviewLitter(null);
  } catch (error) {
    setMessage('Error: ' + error.message);
  }
};

  const previewBreeding = async () => {
    if (!sireId || !damId || !bredBy) {
      setMessage('Please select a sire, dam, and bred-by kennel.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/preview-litter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, sireId, damId, puppyCount, optOutHealthTests, designerUsername, bredBy, coBreederKennelId })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPreviewLitter(data.litter);
      setMessage(data.message);
    } catch (error) {
      setMessage('Error previewing litter: ' + error.message);
    }
  };

  const rollLitter = async (litterId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/roll-litter/${litterId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLitters(litters.map(l => l._id === litterId ? data.litter : l));
      setMessage(data.message || 'Litter rolled successfully');
      navigate(`/litter/${litterId}`);
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const fetchDesignerSuggestions = async (value) => {
    if (!value) {
      setDesignerSuggestions([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/search-players/${encodeURIComponent(value)}`);
      if (!res.ok) throw new Error(await res.text());
      setDesignerSuggestions(await res.json());
    } catch (error) {
      setMessage('Error fetching designer suggestions: ' + error.message);
    }
  };

  const renderHealthTestsTable = (tests, type, parent) => {
    if (!tests || tests.length === 0) {
      return <p>No {type} test results available for {parent}.</p>;
    }
    const isBodyScores = type === 'Body Scores';

    return (
      <div className="health-tests-container">
        <h4>{parent} {type}</h4>
        <table className="health-tests-table">
          <thead>
            <tr>
              <th>{isBodyScores ? 'Category' : 'Disease Name'}</th>
              <th>{isBodyScores ? 'Score' : 'Result'}</th>
              {!isBodyScores && <th>Date Tested</th>}
            </tr>
          </thead>
          <tbody>
            {tests
              .sort((a, b) => a.testName.localeCompare(b.testName))
              .map((test, idx) => (
                <tr key={idx}>
                  <td>{test.testName.replace(isBodyScores ? /^Body Scores\s*-\s*/i : /^BBP\s*-\s*/i, '')}</td>
                  <td>{isBodyScores ? test.score || 'N/A' : test.result}</td>
                  {!isBodyScores && <td>{test.dateTested ? new Date(test.dateTested).toLocaleDateString() : 'N/A'}</td>}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!playerId) return <p>Please log in to breed litters.</p>;

  return (
    <div className="breed-litter-container">
      <h1>Breed a Litter</h1>
      {message && (
        <p className={message.includes('Error') ? 'error-message' : 'success-message'}>
          {message}
        </p>
      )}
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <>
          <LitterForm
            playerId={playerId}
            dogs={dogs}
            kennels={kennels}
            otherKennels={otherKennels}
            dogLookup={dogLookup}
            sireId={sireId}
            damId={damId}
            puppyCount={puppyCount}
            optOutHealthTests={optOutHealthTests}
            designerUsername={designerUsername}
            bredBy={bredBy}
            coBreederKennelId={coBreederKennelId}
            designerSuggestions={designerSuggestions}
            setSireId={setSireId}
            setDamId={setDamId}
            setPuppyCount={setPuppyCount}
            setOptOutHealthTests={setOptOutHealthTests}
            setDesignerUsername={setDesignerUsername}
            setBredBy={setBredBy}
            setCoBreederKennelId={setCoBreederKennelId}
            setDesignerId={setDesignerId}
            setDesignerSuggestions={setDesignerSuggestions}
            setMessage={setMessage}
            setPreviewLitter={setPreviewLitter}
            submitLitter={submitLitter}
            previewBreeding={previewBreeding}
            renderHealthTestsTable={renderHealthTestsTable}
            fetchDesignerSuggestions={fetchDesignerSuggestions}
            previewLitter={previewLitter}
            isInbred={isInbred}
            commonAncestors={commonAncestors}
          />
          <h2>Pending Litters</h2>
          {litters.filter(l => l.status === 'Pending').length === 0 ? (
            <p>No pending litters.</p>
          ) : (
            litters
              .filter(l => l.status === 'Pending')
              .map(litter => (
                <div key={litter._id} className="pending-litter">
                  <p>Litter: {dogLookup[litter.sireId]?.registeredName || litter.sireId} x {dogLookup[litter.damId]?.registeredName || litter.damId}</p>
                  <p>Status: {litter.coBreederApprovalStatus || 'Approved'}</p>
                  <button
                    className="action-button"
                    onClick={() => rollLitter(litter._id)}
                    disabled={litter.coBreederApprovalStatus === 'Pending'}
                  >
                    Roll Litter
                  </button>
                </div>
              ))
          )}
        </>
      )}
    </div>
  );
}

export default BreedLitter;